import { normalizeUserCode } from "./normalizeUserCode.ts";
import { formatSandboxOutput } from "./quickjsSandbox.ts";
import type { ConsoleLogEntry } from "./types.ts";

export interface SandboxExecutionResult {
	ok: boolean;
	data?: unknown;
	error?: unknown;
	logs: ConsoleLogEntry[];
}

export interface ExecuteCallbackParams {
	moduleSource: string;
	input: string;
}

export type ExecuteCallback = (
	params: ExecuteCallbackParams,
) => Promise<SandboxExecutionResult>;

export interface ExecuteUserCodeParams {
	code: string;
	input: string;
	execute: ExecuteCallback;
	now?: () => number;
	formatOutput?: (value: unknown) => string;
}

export interface ExecuteUserCodeSuccess {
	ok: true;
	output: string;
	executionTime: number;
	consoleLogs: ConsoleLogEntry[];
}

export interface ExecuteUserCodeError {
	ok: false;
	error: unknown;
	executionTime: number;
	consoleLogs: ConsoleLogEntry[];
}

export type ExecuteUserCodeResult =
	| ExecuteUserCodeSuccess
	| ExecuteUserCodeError;

/**
 * Shared execution helper that wraps user code, delegates execution to a provided runner, and normalizes results.
 */
export const executeUserCode = async ({
	code,
	input,
	execute,
	now = () => Date.now(),
	formatOutput = formatSandboxOutput,
}: ExecuteUserCodeParams): Promise<ExecuteUserCodeResult> => {
	const startedAt = now();

	const diff = () => now() - startedAt;

	try {
		const moduleSource = normalizeUserCode(code ?? "");
		const result = await execute({ moduleSource, input });
		const executionTime = diff();
		const consoleLogs = result.logs ?? [];

		if (result.ok) {
			return {
				ok: true,
				output: formatOutput(result.data),
				executionTime,
				consoleLogs,
			};
		}

		return {
			ok: false,
			error: result?.error ?? new Error("Unknown execution error"),
			executionTime,
			consoleLogs,
		};
	} catch (error) {
		return {
			ok: false,
			error: error ?? new Error("Unknown execution failure"),
			executionTime: diff(),
			consoleLogs: [],
		};
	}
};
